import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formsApi } from '@/api/formsApi'
import { useTenant } from '@/providers/TenantProvider'
import { useAuditTrailInvalidator } from '@/hooks/useAuditTrail'

const KEY = 'oos-alerts'

/**
 * A submission's out-of-specification alerts (z8nrz7eq4e). Every flagged field
 * on the form reads the same query, so the form fetches the list once.
 */
export function useOosAlerts(submissionId: string, enabled: boolean) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, tenantId, submissionId] as const,
    queryFn: () => formsApi.listOosAlerts(submissionId),
    enabled: enabled && !!tenantId && !!submissionId,
  })
}

/** Refresh the alert list after a save that may have raised or closed one. */
export function useInvalidateOosAlerts() {
  const qc = useQueryClient()
  return useCallback(() => void qc.invalidateQueries({ queryKey: [KEY] }), [qc])
}

/** Acknowledge an alert or record the action taken. */
export function useReviewOosAlert(submissionId: string) {
  const invalidateAlerts = useInvalidateOosAlerts()
  const invalidateAuditTrail = useAuditTrailInvalidator()
  return useMutation({
    mutationFn: (input: { alertId: string; action: 'acknowledge' | 'action_taken'; comment?: string }) =>
      formsApi.reviewOosAlert(submissionId, input.alertId, input.action, input.comment),
    // A refused review (someone corrected the value first) changes the list
    // too, so refresh either way.
    onSettled: () => {
      invalidateAlerts()
      invalidateAuditTrail()
    },
  })
}

/** Add or change the comment on an open alert's value. */
export function useSetOosAlertComment(submissionId: string) {
  const invalidateAlerts = useInvalidateOosAlerts()
  const invalidateAuditTrail = useAuditTrailInvalidator()
  return useMutation({
    mutationFn: (input: { alertId: string; comment: string }) =>
      formsApi.setOosAlertComment(submissionId, input.alertId, input.comment),
    onSettled: () => {
      invalidateAlerts()
      invalidateAuditTrail()
    },
  })
}
